import React from 'react';
import { aboutContent } from '../../content/about-content';

/**
 * Learning Theory Section
 *
 * Explains cognitive science principles behind LRB.
 */

// Icons for learning concepts
const LearningIcons = {
  build: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M17 18h1" />
      <path d="M12 18h1" />
      <path d="M7 18h1" />
    </svg>
  ),
  buffer: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  ),
  scale: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="14" height="14" rx="2" ry="2" />
      <line x1="18" y1="9" x2="12" y2="9" />
      <line x1="15" y1="6" x2="15" y2="12" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
    </svg>
  ),
  shuffle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  )
};

const LearningTheorySection = React.forwardRef(({ id = 'learning' }, ref) => {
  const { learningTheory, citations } = aboutContent;

  const getCitation = (id) => {
    const citation = citations.find(c => c.id === id);
    return citation ? `[${id}]` : '';
  };

  return (
    <section id={id} ref={ref} className="about-section">
      <header className="about-section__header">
        <h2 className="about-section__title">
          <svg className="about-section__title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
          {learningTheory.title}
        </h2>
        <p className="about-section__subtitle">{learningTheory.intro}</p>
      </header>

      <div className="info-cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {learningTheory.concepts.map((concept, index) => (
          <div key={index} className="info-card">
            <div className="info-card__icon">
              {LearningIcons[concept.icon] || LearningIcons.build}
            </div>
            <h3 className="info-card__title">
              {concept.name}
              {concept.citation && (
                <sup className="citation-ref" style={{ marginLeft: '4px' }}>
                  <a href={`#citation-${concept.citation}`}>
                    {getCitation(concept.citation)}
                  </a>
                </sup>
              )}
            </h3>
            <p className="info-card__content">{concept.description}</p>
            <p className="info-card__example">
              <strong>In practice:</strong> {concept.example}
            </p>
          </div>
        ))}
      </div>

      {/* Working Memory Visualization */}
      <div style={{
        marginTop: '2.5rem',
        padding: '1.5rem',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '1px solid var(--lrbrain-border-light)'
      }}>
        <h3 style={{
          fontFamily: 'var(--lrbrain-font-mono)',
          fontSize: '0.85rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--lrbrain-navy)',
          margin: '0 0 1rem 0'
        }}>
          Working Memory Capacity
        </h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: 'var(--lrbrain-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontFamily: 'var(--lrbrain-font-mono)',
                fontWeight: '700',
                fontSize: '1.25rem'
              }}
            >
              {i}
            </div>
          ))}
          <span style={{
            fontFamily: 'var(--lrbrain-font-mono)',
            fontSize: '1.5rem',
            color: 'var(--lrbrain-text-light)',
            margin: '0 0.25rem'
          }}>
            ±1
          </span>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '8px',
              background: 'var(--lrbrain-bg)',
              border: '2px dashed var(--lrbrain-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--lrbrain-text-light)',
              fontFamily: 'var(--lrbrain-font-mono)',
              fontWeight: '700',
              fontSize: '1.25rem'
            }}
          >
            ?
          </div>
        </div>
        <p style={{
          marginTop: '1rem',
          fontSize: '0.85rem',
          color: 'var(--lrbrain-text-light)',
          fontStyle: 'italic'
        }}>
          The average person can hold 4±1 items in working memory at once. Overload this and learning stops.
        </p>
      </div>
    </section>
  );
});

LearningTheorySection.displayName = 'LearningTheorySection';

export default LearningTheorySection;
